<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import Popper from 'vue3-popper'
import PaginatedTable from '@/app/components/PaginatedTable.vue'
import {
  DBtn,
  DMultiselect,
  DSelectFieldSearch,
  DModal,
  DLoading,
  DToggleField,
  InfoCircleIcon
} from 'front-library'
import {
  getPayroll,
  getConcepts,
  createCopyPayrollRequest
} from '@/app/core/services/payroll-copy-requests'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const storePayroll = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const loading = ref(false)

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsOriginPayrollId = ref([] as any[])
const searchOriginPayrollId = ref('')
const dataPayrollOrigin = ref('' as any)
const itemsDestinationPayrollId = ref([] as any[])
const searchDestinationPayrollId = ref('')
const dataPayrollDestination = ref('' as any)
const disabledDestinationPayrollId = ref(true)
const conceptsStandard = ref([] as any[])
const itemsConfigFields = ref([
  {
    id: 'credit_account_id',
    name: t('payrollCopyRequests.copy.creditAccount')
  },
  { id: 'debit_account_id', name: t('payrollCopyRequests.copy.debitAccount') },
  { id: 'name_alternate', name: t('payrollCopyRequests.copy.nameAlternate') },
  { id: 'code_alternate', name: t('payrollCopyRequests.copy.codeAlternate') },
  { id: 'journeys', name: t('payrollCopyRequests.copy.journeys') },
  { id: 'bases', name: t('payrollCopyRequests.copy.calculationBasis') }
] as any[])
const disabledMarkOnlyStandard = ref(true)
const finalValues = ref()

/* Datos componente table */
const columns = ref([
  { key: 'name', title: t('payrollCopyRequests.list.name'), align: 'left' },
  { key: 'code', title: t('payrollCopyRequests.list.code'), align: 'left' },
  { key: 'format', title: t('payrollCopyRequests.list.format'), align: 'left' },
  {
    key: 'is_standard',
    title: t('payrollCopyRequests.list.standard'),
    align: 'left'
  }
])
const rows = ref([] as any[])
const tableCopyPayrollRequest = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  originPayrollId: string().required(t('form.valid.required')),
  destinationPayrollId: string().required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  validationSchema
})

/* Definicion campos del formulario */
const { value: originPayrollId } = useField<string>(ref('originPayrollId'))
const { value: destinationPayrollId } = useField<string>(
  ref('destinationPayrollId')
)
const { value: configFields } = useField<any[]>(ref('configFields'))
const { value: isUpgradable } = useField<boolean>(ref('isUpgradable'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  loading.value = true
  values.concepts =
    tableCopyPayrollRequest.value.selectedRows.length === rows.value.length
      ? []
      : tableCopyPayrollRequest.value.selectedRows
  finalValues.value = values
  if (values.isUpgradable) {
    loading.value = false
    showModal.value = true
    descriptionModal.value = t(
      'payrollCopyRequests.copy.updateExistingConcepts'
    )
  } else {
    void creationRequestCopyConcepts(finalValues.value)
  }
})

/* Filtro de nómina destino */
const filterPayrollDestination = async (payrollOrigin: any) => {
  loading.value = true
  dataPayrollOrigin.value = itemsOriginPayrollId.value.filter(
    (_: any) => _.id === payrollOrigin
  )[0]
  tableCopyPayrollRequest.value.selectedRows = []
  tableCopyPayrollRequest.value.selectedAllRows = false
  if (dataPayrollOrigin.value) {
    const p1 = new Promise(resolve => {
      resolve(
        getPayroll(
          store.user?.id as string,
          storePayroll.payroll?.country.id,
          payrollOrigin
        )
      )
    })
    const p2 = new Promise(resolve => {
      resolve(getConcepts(payrollOrigin))
    })
    Promise.all([p1, p2])
      .then(async (results: any) => {
        itemsDestinationPayrollId.value = results[0]
        rows.value = results[1]
        conceptsStandard.value = rows.value.filter(
          (_: any) => _.is_standard === 'Si'
        )
        itemsDestinationPayrollId.value.unshift({
          id: null,
          name: t('form.button.select')
        })
        disabledDestinationPayrollId.value = false
        disabledMarkOnlyStandard.value = !(rows.value.length > 0)
        if (destinationPayrollId.value === originPayrollId.value) {
          dataPayrollDestination.value = []
          destinationPayrollId.value = ''
        }
        loading.value = false
      })
      .catch(function (error: any) {
        notificationError(
          error,
          'payrollCopyRequests',
          t,
          notifications,
          loading
        )
      })
  } else {
    dataPayrollOrigin.value = []
    itemsDestinationPayrollId.value = []
    disabledDestinationPayrollId.value = true
    disabledMarkOnlyStandard.value = true
    conceptsStandard.value = []
    rows.value = []
    loading.value = false
  }
}

/* Consultar información de nómina destino */
const payrollDestinationData = (payrollDestination: any) => {
  dataPayrollDestination.value = itemsOriginPayrollId.value.filter(
    (_: any) => _.id === payrollDestination
  )[0]
  if (!dataPayrollDestination.value) {
    dataPayrollDestination.value = []
  }
}

/*  */
const checkedStandard = () => {
  tableCopyPayrollRequest.value.selectedRows = []
  tableCopyPayrollRequest.value.selectedAllRows = false
  conceptsStandard.value.forEach(item => {
    tableCopyPayrollRequest.value.selectedRows.push(item.id)
  })
}

/* creación de solicitud de copiado de conceptos */
const creationRequestCopyConcepts = async (values: any) => {
  loading.value = true
  showModal.value = false
  await createCopyPayrollRequest(values)
    .then(async (results: any) => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.edited'),
        text: t('payrollCopyRequests.notification.action', [
          t('notification.wasCreated')
        ]),
        type: 'success',
        time: 10000
      })
      void router.push({ name: 'ListCopyPayrollRequest' })
    })
    .catch(function (error: any) {
      notificationError(error, 'payrollCopyRequests', t, notifications, loading)
    })
}

/* Cerrar modal */
const closeModal = () => {
  finalValues.value = ''
  showModal.value = false
}

const back = () => {
  void router.push({ name: 'ListCopyPayrollRequest' })
}

onMounted(async () => {
  loading.value = true
  try {
    itemsOriginPayrollId.value = await getPayroll(store.user?.id as string)
    itemsOriginPayrollId.value.unshift({
      id: null,
      name: t('form.button.select')
    })
    configFields.value = itemsConfigFields.value.map(item => item.id)
    isUpgradable.value = false
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('payrollCopyRequests.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    } else {
      notificationError(error, 'payrollCopyRequests', t, notifications, loading)
    }
  }
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="t('loading.data')" />
    <DModal
      :open="showModal"
      typeAlert="question"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal"
      @otherMethod="creationRequestCopyConcepts(finalValues)" />
    <div class="bg-gray-50 pb-10">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('payrollCopyRequests.create')" />
      </div>
    </div>
    <form
      class="scroll overflow-auto"
      style="height: calc(100% - 7rem)"
      @submit.prevent="onSubmit">
      <div class="flex flex-col">
        <div
          class="flex flex-col justify-between border-2 sm:w-full rounded-lg bg-white h-auto min-h-0">
          <div class="p-6">
            <div class="grid grid-cols-2 gap-4">
              <div class="sm:col-span-2 lg:col-span-1">
                <DSelectFieldSearch
                  :label="t('payrollCopyRequests.fields.sourcePayroll')"
                  v-model="originPayrollId"
                  :required="true"
                  :options="
                    filterOptions(itemsOriginPayrollId, searchOriginPayrollId)
                  "
                  @filter="searchOriginPayrollId = $event"
                  @change="filterPayrollDestination(originPayrollId)"
                  :error="errors.originPayrollId" />
                <div
                  class="flex flex-col justify-between border-2 sm:w-full rounded-lg bg-gray-100 h-auto min-h-0 p-3 mt-4">
                  <div class="grid grid-cols-1 gap-1 text-sm">
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.code') }}</b>
                      {{ dataPayrollOrigin.code }}
                    </p>
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.name') }}</b>
                      {{ dataPayrollOrigin.payroll }}
                    </p>
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.frequency') }}</b>
                      {{ dataPayrollOrigin.frequency }}
                    </p>
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.currency') }}</b>
                      {{ dataPayrollOrigin.currency }}
                    </p>
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.company') }}</b>
                      {{ dataPayrollOrigin.company }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="sm:col-span-2 lg:col-span-1">
                <DSelectFieldSearch
                  :label="t('payrollCopyRequests.fields.destinationPayroll')"
                  v-model="destinationPayrollId"
                  :required="true"
                  :disabled="disabledDestinationPayrollId"
                  :options="
                    filterOptions(
                      itemsDestinationPayrollId,
                      searchDestinationPayrollId
                    )
                  "
                  @filter="searchDestinationPayrollId = $event"
                  @change="payrollDestinationData(destinationPayrollId)"
                  :error="errors.destinationPayrollId" />
                <div
                  class="flex flex-col justify-between border-2 sm:w-full rounded-lg bg-gray-100 h-auto min-h-0 p-3 mt-4">
                  <div class="grid grid-cols-1 gap-1 text-sm">
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.code') }}</b>
                      {{ dataPayrollDestination.code }}
                    </p>
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.name') }}</b>
                      {{ dataPayrollDestination.payroll }}
                    </p>
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.frequency') }}</b>
                      {{ dataPayrollDestination.frequency }}
                    </p>
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.currency') }}</b>
                      {{ dataPayrollDestination.currency }}
                    </p>
                    <p>
                      <b>{{ t('payrollCopyRequests.copy.company') }}</b>
                      {{ dataPayrollDestination.company }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <footer class="bg-gray-50 rounded-b-lg py-3 px-6">
            <div class="grid grid-cols-2 lg:grid-cols-4 items-center gap-4">
              <p class="text-lg">
                <b>{{
                  t('payrollCopyRequests.copy.standardConcepts') +
                  ' ' +
                  conceptsStandard.length
                }}</b>
              </p>
              <div></div>
              <div class="w-fit flex items-center gap-2">
                <DToggleField
                  v-model="isUpgradable"
                  :label="t('payrollCopyRequests.fields.isUpdatable')" />
                <Popper
                  arrow
                  offsetDistance="12"
                  :content="t('payrollCopyRequests.copy.existingConcepts')"
                  :hover="true"
                  placement="top"
                  class="tooltip">
                  <InfoCircleIcon class="w-5 h-5" />
                </Popper>
              </div>
              <div class="flex justify-end">
                <DBtn
                  class="px-5 py-2 font-semibold w-52"
                  v-html="t('payrollCopyRequests.fields.copyConcepts')" />
              </div>
            </div>
          </footer>
        </div>
        <div
          class="flex flex-col justify-between border-2 sm:w-full rounded-lg bg-white h-auto min-h-0 mt-4">
          <main class="py-2 flex flex-col gap-3">
            <div class="h-full px-6 py-3 overflow-auto scroll">
              <div class="grid grid-span-2 lg:grid-cols-4 gap-4 pb-3">
                <div class="col-span-2 flex items-center">
                  <DBtn
                    type="button"
                    class="font-semibold py-1 text-base"
                    :color="disabledMarkOnlyStandard ? 'disabled' : 'secondary'"
                    @click="checkedStandard"
                    v-html="t('payrollCopyRequests.fields.markStandard')" />
                </div>
                <div class="col-span-2">
                  <DMultiselect
                    class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                    :label="t('payrollCopyRequests.fields.includeFields')"
                    v-model="configFields"
                    :headerTitle="t('payrollCopyRequests.fields.includeFields')"
                    :options="itemsConfigFields" />
                </div>
              </div>
              <PaginatedTable
                ref="tableCopyPayrollRequest"
                :columns="columns"
                :rows="rows"
                :enable-select-rows="true"
                height-table="calc(100vh - 19rem)" />
            </div>
            <footer class="bg-gray-50 rounded-b-lg pt-3 pb-1 pr-3">
              <div class="flex justify-end gap-3">
                <DBtn
                  type="button"
                  class="font-semibold py-1 text-base"
                  color="secondary"
                  @click="back()"
                  v-html="t('form.button.back')" />
              </div>
            </footer>
          </main>
        </div>
      </div>
    </form>
  </div>
</template>
